import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ImportWizard } from './ImportWizard';
import { TestSelectorPanel } from './TestSelector/TestSelectorPanel';
import { ClassificationCalibrationTool } from './ClassificationCalibrationTool';
import { BodePlot } from './Charts/BodePlot';
import { NyquistPlot } from './Charts/NyquistPlot';
import { DeltaPlot } from './Charts/DeltaPlot';
import { CombinedPlot, CHANNEL_COLOR_MAP } from './Charts/CombinedPlot';
import { ChartControls } from './Charts/ChartControls';
import { ClassificationPanel } from './Charts/ClassificationPanel';
import { useChartData, generateTemporalPalette } from './Charts/useChartData';
import { computeDelta, exportToCsv, exportToJson, downloadFile, getExportFilename, getPerTestExportFilename } from '@/lib/impedance';
import { compositeToBlob, compositeAndDownload } from '@/lib/impedance/imageCompositor';
import { zipSync } from 'fflate';
import type { TestChartData, MeasurementData } from '@/lib/impedance';
import type { ReferenceLabelDto } from '@/types/referenceLabels';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { testService } from '@/services/api/testService';
import type { PathogenClassificationResult, TestDetailResponse } from '@/services/api/testService';
import { referenceLabelService } from '@/services/api/referenceLabelService';
import { signalRService } from '@/services/signalRService';
import { useHasAnyRole } from '@/store/useAuthStore';
import { features } from '@/config/features';
import {
  useAnalysisTestSelectionStore,
  selectSelectedTestIds,
  selectSelectorPanelWidth,
} from '@/store/useAnalysisTestSelectionStore';

function buildLabelMap(
  testIds: string[],
  results: PromiseSettledResult<ReferenceLabelDto[]>[],
): Map<string, Map<number, ReferenceLabelDto>> {
  const map = new Map<string, Map<number, ReferenceLabelDto>>();
  testIds.forEach((testId, i) => {
    const inner = new Map<number, ReferenceLabelDto>();
    const result = results[i];
    if (result.status === 'fulfilled') {
      for (const dto of result.value) {
        if (dto.referenceLabel !== null) inner.set(dto.channelId, dto);
      }
    }
    map.set(testId, inner);
  });
  return map;
}

interface AnalysisState {
  chartType: 'bode' | 'nyquist' | 'delta' | 'combined' | 'calibration';
  viewMode: 'baselines' | 'specimens' | 'both';
  visibleChannels: Set<string>;
  phaseUnit: 'rad' | 'deg';
  yScale: 'linear' | 'log';
  deltaMode: 'bode' | 'nyquist';
}

function buildDeltaTestData(testData: TestChartData[]): TestChartData[] {
  return testData
    .map((t) => {
      if (!t.baseline || !t.specimen) return null;
      try {
        const deltaChannels = computeDelta(t.baseline, t.specimen);
        const deltaData: MeasurementData = {
          channels: deltaChannels,
          frequencies: t.baseline.frequencies,
          channelCount: t.baseline.channelCount,
        };
        return { testId: t.testId, label: t.label, notes: t.notes, baseline: deltaData, specimen: null } as TestChartData;
      } catch {
        return null;
      }
    })
    .filter((x): x is NonNullable<typeof x> => x !== null) as TestChartData[];
}

export function MeasurementAnalysisPage() {
  const [importOpen, setImportOpen] = useState(false);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [labelFetchWarning, setLabelFetchWarning] = useState<{
    failedTestIds: string[];
    onProceed: () => void;
  } | null>(null);
  const queryClient = useQueryClient();

  // Refs capturing the Plotly graphDiv per chart type — used for PNG/SVG export
  const bodeRef = useRef<HTMLElement | null>(null);
  const nyquistRef = useRef<HTMLElement | null>(null);
  const deltaRef = useRef<HTMLElement | null>(null);
  const combinedRef = useRef<HTMLElement | null>(null);

  // Ref for the ClassificationPanel DOM node — composited into exported images
  const classificationPanelRef = useRef<HTMLDivElement | null>(null);

  // Per-test isolated hidden chart state — driven by the export loops in handleExport / handleBulkExport
  const [hiddenExportTestId, setHiddenExportTestId] = useState<string | null>(null);
  const [hiddenExportViewType, setHiddenExportViewType] = useState<'bode' | 'nyquist' | 'combined' | 'delta'>('bode');
  const [hiddenExportTestData, setHiddenExportTestData] = useState<TestChartData[]>([]);
  const [hiddenClassificationResults, setHiddenClassificationResults] = useState<PathogenClassificationResult[] | null>(null);
  const [hiddenReferenceLabels, setHiddenReferenceLabels] = useState<Map<number, ReferenceLabelDto> | undefined>(undefined);
  const hiddenPlotRef = useRef<HTMLElement | null>(null);
  const hiddenClassificationRef = useRef<HTMLDivElement | null>(null);
  // Resolver fired by onPlotRef when the hidden chart finishes mounting
  const hiddenPlotResolver = useRef<(() => void) | null>(null);

  const selectedTestIds = useAnalysisTestSelectionStore(selectSelectedTestIds);
  const selectorPanelWidth = useAnalysisTestSelectionStore(selectSelectorPanelWidth);
  const setSelectorPanelWidth = useAnalysisTestSelectionStore((s) => s.setSelectorPanelWidth);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => { dragCleanupRef.current?.(); }, []);

  const [state, setState] = useState<AnalysisState>({
    chartType: 'combined',
    viewMode: 'both',
    visibleChannels: new Set(['CH1', 'CH2', 'CH3', 'CH4']),
    phaseUnit: 'rad',
    yScale: 'linear',
    deltaMode: 'bode',
  });

  const { testData, isLoading, error } = useChartData(selectedTestIds);

  const deltaTestData = useMemo(() => buildDeltaTestData(testData), [testData]);

  // Fetch classification results for the single selected test (any chart type)
  const singleTestId =
    selectedTestIds.length === 1
      ? selectedTestIds[0]
      : null;
  const { data: classificationTestDetail } = useQuery({
    queryKey: ['test-detail', singleTestId],
    queryFn: () => testService.getTestById(singleTestId!),
    enabled: singleTestId != null,
    staleTime: 5 * 60 * 1000,
  });

  // When classification completes for the active test, refresh both the detail
  // panel and the sidebar list so results and status update automatically.
  useEffect(() => {
    if (!singleTestId) return;
    let unsubscribe: (() => void) | null = null;
    signalRService.connect().then(() => {
      unsubscribe = signalRService.subscribeToTestComplete(singleTestId, () => {
        void queryClient.invalidateQueries({ queryKey: ['test-detail', singleTestId] });
        void queryClient.invalidateQueries({ queryKey: ['tests-for-analysis'] });
      });
    }).catch(() => {});
    return () => { unsubscribe?.(); };
  }, [singleTestId, queryClient]);

  // RESEARCH FEATURE: Load reference labels for the single selected test
  const isResearcher = useHasAnyRole('researcher', 'admin');
  const { data: referenceLabels } = useQuery({
    queryKey: ['reference-labels', singleTestId],
    queryFn: () => referenceLabelService.getLabels(singleTestId!),
    enabled: singleTestId != null && isResearcher && features.enableResearchFeatures,
    staleTime: 5 * 60 * 1000,
  });

  const referenceLabelsMap = useMemo(() => {
    if (!referenceLabels) return undefined;
    const map = new Map<number, ReferenceLabelDto>();
    for (const l of referenceLabels) {
      if (l.referenceLabel != null) map.set(l.channelId, l);
    }
    return map;
  }, [referenceLabels]);

  // Build color map based on the order test IDs appear in selectedTestIds
  const colorMap = useMemo<Map<string, string>>(() => {
    const colors = generateTemporalPalette(selectedTestIds.length);
    return new Map(selectedTestIds.map((id, i) => [id, colors[i]]));
  }, [selectedTestIds]);

  const handleChannelToggle = useCallback((channel: string, checked: boolean) => {
    setState((prev) => {
      const next = new Set(prev.visibleChannels);
      if (checked) next.add(channel);
      else next.delete(channel);
      return { ...prev, visibleChannels: next };
    });
  }, []);

  const displayTestData = useMemo<TestChartData[]>(() => {
    if (state.chartType === 'delta') {
      return deltaTestData;
    }
    return testData;
  }, [testData, deltaTestData, state.chartType]);

  const isLabeledExport = features.enableResearchFeatures && isResearcher;

  const handleExport = useCallback(
    async (format: 'csv' | 'json' | 'png' | 'svg') => {
      const exportMode = state.chartType === 'calibration' ? 'bode' : state.chartType;

      if (format === 'png' || format === 'svg') {
        if (displayTestData.length === 0) return;

        // N=1 fast path: live chart already shows only this one test
        if (displayTestData.length === 1) {
          const plotDiv =
            state.chartType === 'bode'
              ? bodeRef.current
              : state.chartType === 'nyquist'
                ? nyquistRef.current
                : state.chartType === 'combined'
                  ? combinedRef.current
                  : deltaRef.current;
          if (!plotDiv) return;
          setExportLoading(true);
          try {
            const filename = getPerTestExportFilename(exportMode, displayTestData[0].label, format);
            await compositeAndDownload(
              {
                plotDiv,
                classificationPanelEl: classificationPanelRef.current,
                format,
                width: 1600,
                height: 700,
              },
              filename,
            );
          } finally {
            setExportLoading(false);
          }
          return;
        }

        // N>1: render each test in isolation, then ZIP
        setExportLoading(true);
        setIsExporting(true);
        try {
          const files: Record<string, Uint8Array> = {};
          const failedTestIds: string[] = [];
          const usedFilenames = new Set<string>();
          const date = new Date().toISOString().slice(0, 10);

          for (const test of displayTestData) {
            let classificationResults: PathogenClassificationResult[] | null = null;
            let testReferenceLabels: Map<number, ReferenceLabelDto> | undefined;
            try {
              const cached = queryClient.getQueryData<TestDetailResponse>(['test-detail', test.testId]);
              const detail = cached ?? await testService.getTestById(test.testId);
              if (detail.pathogenClassificationResults && detail.pathogenClassificationResults.length > 0) {
                classificationResults = detail.pathogenClassificationResults;
              }
            } catch {
              failedTestIds.push(test.testId);
            }

            if (isLabeledExport && classificationResults) {
              try {
                const cachedLabels = queryClient.getQueryData<ReferenceLabelDto[]>(['reference-labels', test.testId]);
                const labels = cachedLabels ?? await referenceLabelService.getLabels(test.testId);
                const map = new Map<number, ReferenceLabelDto>();
                for (const l of labels) {
                  if (l.referenceLabel != null) map.set(l.channelId, l);
                }
                if (map.size > 0) testReferenceLabels = map;
              } catch {
                // fail silently — chart-only export continues without reference labels
              }
            }

            const plotReady = new Promise<void>((resolve) => {
              hiddenPlotResolver.current = resolve;
            });
            setHiddenClassificationResults(classificationResults);
            setHiddenReferenceLabels(testReferenceLabels);
            setHiddenExportTestData([test]);
            setHiddenExportViewType(exportMode === 'bode' ? 'bode' : exportMode === 'nyquist' ? 'nyquist' : exportMode === 'delta' ? 'delta' : 'combined');
            setHiddenExportTestId(`${test.testId}-${exportMode}`);

            const resolved = await Promise.race([
              plotReady.then(() => true as const),
              new Promise<false>((r) => setTimeout(() => r(false), 5000)),
            ]);
            hiddenPlotResolver.current = null;

            if (!resolved || !hiddenPlotRef.current) {
              failedTestIds.push(test.testId);
              continue;
            }

            try {
              const blob = await compositeToBlob({
                plotDiv: hiddenPlotRef.current,
                classificationPanelEl: hiddenClassificationRef.current,
                format,
                width: 1600,
                height: 700,
              });

              let filename = getPerTestExportFilename(exportMode, test.label, format);
              if (usedFilenames.has(filename)) {
                const dotIdx = filename.lastIndexOf('.');
                let counter = 2;
                while (usedFilenames.has(filename)) {
                  filename = `${filename.slice(0, dotIdx)}-${counter}${filename.slice(dotIdx)}`;
                  counter++;
                }
              }
              usedFilenames.add(filename);
              files[filename] = new Uint8Array(await blob.arrayBuffer());
            } catch {
              failedTestIds.push(test.testId);
            }
          }

          setHiddenExportTestId(null);

          if (Object.keys(files).length === 0) {
            setExportLoading(false);
            return;
          }

          const doZip = () => {
            const zipped = zipSync(files);
            const zipBlob = new Blob([zipped], { type: 'application/zip' });
            const url = URL.createObjectURL(zipBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `labcloud-analysis-export-${date}.zip`;
            a.click();
            URL.revokeObjectURL(url);
          };

          if (failedTestIds.length > 0) {
            setLabelFetchWarning({
              failedTestIds,
              onProceed: () => {
                setLabelFetchWarning(null);
                doZip();
                setExportLoading(false);
              },
            });
          } else {
            doZip();
            setExportLoading(false);
          }
        } catch {
          setExportLoading(false);
        } finally {
          setIsExporting(false);
        }
        return;
      }

      if (format !== 'csv' && format !== 'json') return;

      if (!isLabeledExport) {
        if (format === 'csv') {
          const csv = exportToCsv(displayTestData, exportMode, state.visibleChannels, state.viewMode);
          downloadFile(csv, getExportFilename(exportMode, 'csv'), 'text/csv;charset=utf-8;');
        } else {
          const json = exportToJson(displayTestData, exportMode, state.visibleChannels, state.viewMode);
          downloadFile(json, getExportFilename(exportMode, 'json'), 'application/json');
        }
        return;
      }

      setExportLoading(true);
      try {
        // Determine which test IDs need fetches. Reuse the React Query cache
        // for the single selected test if already populated to avoid a redundant API call.
        const cachedForSingle =
          singleTestId != null
            ? (queryClient.getQueryData<ReferenceLabelDto[]>(['reference-labels', singleTestId]) ?? null)
            : null;

        const idsToFetch = selectedTestIds.filter(
          (id) => !(cachedForSingle !== null && id === singleTestId),
        );

        const fetchResults = await Promise.allSettled(
          idsToFetch.map((id) => referenceLabelService.getLabels(id)),
        );

        // Merge cache result with fetched results into a single settled array
        // ordered by selectedTestIds
        const allResults: PromiseSettledResult<ReferenceLabelDto[]>[] = selectedTestIds.map((id) => {
          if (cachedForSingle !== null && id === singleTestId) {
            return { status: 'fulfilled', value: cachedForSingle };
          }
          const idx = idsToFetch.indexOf(id);
          return fetchResults[idx];
        });

        const failedTestIds = selectedTestIds.filter(
          (_, i) => allResults[i].status === 'rejected',
        );

        const labelMap = buildLabelMap(selectedTestIds, allResults);

        const generateAndDownload = (map: Map<string, Map<number, ReferenceLabelDto>>) => {
          if (format === 'csv') {
            const csv = exportToCsv(displayTestData, exportMode, state.visibleChannels, state.viewMode, map);
            downloadFile(csv, getExportFilename(exportMode, 'csv', true), 'text/csv;charset=utf-8;');
          } else {
            const json = exportToJson(displayTestData, exportMode, state.visibleChannels, state.viewMode, map);
            downloadFile(json, getExportFilename(exportMode, 'json', true), 'application/json');
          }
          setExportLoading(false);
        };

        if (failedTestIds.length > 0) {
          setLabelFetchWarning({
            failedTestIds,
            onProceed: () => {
              setLabelFetchWarning(null);
              generateAndDownload(labelMap);
            },
          });
        } else {
          generateAndDownload(labelMap);
        }
      } catch {
        setExportLoading(false);
      }
    },
    [displayTestData, state.chartType, state.visibleChannels, state.viewMode, selectedTestIds, isLabeledExport, singleTestId, queryClient],
  );

  const handleBulkExport = useCallback(
    async (format: 'png' | 'svg') => {
      if (testData.length === 0) return;

      const allViews: Array<'bode' | 'nyquist' | 'combined' | 'delta'> = ['bode', 'nyquist', 'combined', 'delta'];
      const deltaTestIds = new Set(deltaTestData.map((t) => t.testId));

      setExportLoading(true);
      setIsExporting(true);

      try {
        const files: Record<string, Uint8Array> = {};
        const failedTestIds: string[] = [];
        const usedFilenames = new Set<string>();
        const date = new Date().toISOString().slice(0, 10);

        for (const test of testData) {
          let classificationResults: PathogenClassificationResult[] | null = null;
          let testReferenceLabels: Map<number, ReferenceLabelDto> | undefined;
          try {
            const cached = queryClient.getQueryData<TestDetailResponse>(['test-detail', test.testId]);
            const detail = cached ?? await testService.getTestById(test.testId);
            if (detail.pathogenClassificationResults && detail.pathogenClassificationResults.length > 0) {
              classificationResults = detail.pathogenClassificationResults;
            }
          } catch {
            failedTestIds.push(test.testId);
          }

          if (isLabeledExport && classificationResults) {
            try {
              const cachedLabels = queryClient.getQueryData<ReferenceLabelDto[]>(['reference-labels', test.testId]);
              const labels = cachedLabels ?? await referenceLabelService.getLabels(test.testId);
              const map = new Map<number, ReferenceLabelDto>();
              for (const l of labels) {
                if (l.referenceLabel != null) map.set(l.channelId, l);
              }
              if (map.size > 0) testReferenceLabels = map;
            } catch {
              // fail silently — chart-only export continues without reference labels
            }
          }

          for (const view of allViews) {
            if (view === 'delta' && !deltaTestIds.has(test.testId)) continue;

            const viewTestData: TestChartData[] =
              view === 'delta'
                ? deltaTestData.filter((t) => t.testId === test.testId)
                : [test];

            if (viewTestData.length === 0) continue;

            const plotReady = new Promise<void>((resolve) => {
              hiddenPlotResolver.current = resolve;
            });
            setHiddenClassificationResults(classificationResults);
            setHiddenReferenceLabels(testReferenceLabels);
            setHiddenExportTestData(viewTestData);
            setHiddenExportViewType(view);
            setHiddenExportTestId(`${test.testId}-${view}`);

            const resolved = await Promise.race([
              plotReady.then(() => true as const),
              new Promise<false>((r) => setTimeout(() => r(false), 5000)),
            ]);
            hiddenPlotResolver.current = null;

            if (!resolved || !hiddenPlotRef.current) {
              failedTestIds.push(test.testId);
              continue;
            }

            try {
              const blob = await compositeToBlob({
                plotDiv: hiddenPlotRef.current,
                classificationPanelEl: hiddenClassificationRef.current,
                format,
                width: 1600,
                height: 700,
              });

              let filename = getPerTestExportFilename(view, test.label, format);
              if (usedFilenames.has(filename)) {
                const dotIdx = filename.lastIndexOf('.');
                let counter = 2;
                while (usedFilenames.has(filename)) {
                  filename = `${filename.slice(0, dotIdx)}-${counter}${filename.slice(dotIdx)}`;
                  counter++;
                }
              }
              usedFilenames.add(filename);
              files[filename] = new Uint8Array(await blob.arrayBuffer());
            } catch {
              failedTestIds.push(test.testId);
            }
          }
        }

        setHiddenExportTestId(null);

        if (Object.keys(files).length === 0) {
          setExportLoading(false);
          return;
        }

        const doZip = () => {
          const zipped = zipSync(files);
          const zipBlob = new Blob([zipped], { type: 'application/zip' });
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `labcloud-analysis-export-${date}.zip`;
          a.click();
          URL.revokeObjectURL(url);
        };

        if (failedTestIds.length > 0) {
          setLabelFetchWarning({
            failedTestIds,
            onProceed: () => {
              setLabelFetchWarning(null);
              doZip();
              setExportLoading(false);
            },
          });
        } else {
          doZip();
          setExportLoading(false);
        }
      } catch {
        setExportLoading(false);
      } finally {
        setIsExporting(false);
      }
    },
    [testData, deltaTestData, queryClient, isLabeledExport],
  );

  const traceCount = displayTestData.reduce((acc) => {
    const types =
      state.viewMode === 'both' ? 2 : 1;
    const channels = Array.from(state.visibleChannels).length;
    return acc + types * channels;
  }, 0);

  const hasChartData = testData.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Measurement Analysis
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={exportLoading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
            endIcon={<KeyboardArrowDownIcon fontSize="small" />}
            onClick={(e) => !exportLoading && setExportAnchor(e.currentTarget)}
            disabled={exportLoading}
          >
            {exportLoading ? 'Exporting…' : 'Export'}
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            {(['csv', 'json', 'png', 'svg'] as const).map((fmt) => (
              <MenuItem
                key={fmt}
                onClick={() => {
                  setExportAnchor(null);
                  void handleExport(fmt);
                }}
              >
                {fmt.toUpperCase()}
              </MenuItem>
            ))}
            {hasChartData && <Divider />}
            {hasChartData && (
              <MenuItem
                onClick={() => {
                  setExportAnchor(null);
                  void handleBulkExport('png');
                }}
              >
                All Charts (PNG ZIP)
              </MenuItem>
            )}
            {hasChartData && (
              <MenuItem
                onClick={() => {
                  setExportAnchor(null);
                  void handleBulkExport('svg');
                }}
              >
                All Charts (SVG ZIP)
              </MenuItem>
            )}
          </Menu>
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => setImportOpen(true)}
          >
            Import…
          </Button>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
        {/* Left: Test Selector */}
        <Box sx={{ display: 'flex', flexShrink: 0, position: 'relative' }}>
            <Paper
              elevation={0}
              sx={{
                width: selectorPanelWidth,
                flexShrink: 0,
                borderRight: 0,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <TestSelectorPanel colorMap={colorMap} calibrationMode={state.chartType === 'calibration'} />
            </Paper>
            <Box
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startW = selectorPanelWidth;
                const onMove = (mv: MouseEvent) => setSelectorPanelWidth(startW + mv.clientX - startX);
                const cleanup = () => {
                  document.removeEventListener('mousemove', onMove);
                  document.removeEventListener('mouseup', onUp);
                  dragCleanupRef.current = null;
                };
                const onUp = () => cleanup();
                dragCleanupRef.current = cleanup;
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
              }}
              sx={{
                width: 4,
                flexShrink: 0,
                cursor: 'col-resize',
                bgcolor: 'divider',
                '&:hover': { bgcolor: 'primary.main', opacity: 0.6 },
                transition: 'background-color 0.15s',
              }}
            />
          </Box>

        {/* Right: Chart + Controls (always visible for tab navigation) */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Calibration tool — replaces chart area */}
          {state.chartType === 'calibration' && (
            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
              <ClassificationCalibrationTool />
            </Box>
          )}

          {/* Chart area — hidden in calibration mode */}
          {state.chartType !== 'calibration' && <Box sx={{ flexGrow: 1, position: 'relative', minHeight: 300 }}>
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  bgcolor: 'background.paper',
                  opacity: 0.8,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ m: 2 }}>
                Failed to load impedance data
              </Alert>
            )}

            {!isLoading && displayTestData.length === 0 && selectedTestIds.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body1">
                  Select tests from the panel on the left to visualize impedance spectra.
                </Typography>
              </Box>
            )}

            {displayTestData.length > 0 && state.chartType === 'bode' && (
              <BodePlot
                testData={displayTestData}
                viewMode={state.viewMode}
                visibleChannels={state.visibleChannels}
                phaseUnit={state.phaseUnit}
                yScale={state.yScale}
                colorMap={colorMap}
                onPlotRef={(el) => { bodeRef.current = el; }}
              />
            )}

            {displayTestData.length > 0 && state.chartType === 'delta' && (
              <DeltaPlot
                testData={displayTestData}
                visibleChannels={state.visibleChannels}
                phaseUnit={state.phaseUnit}
                colorMap={colorMap}
                deltaMode={state.deltaMode}
                onPlotRef={(el) => { deltaRef.current = el; }}
              />
            )}

            {displayTestData.length > 0 && state.chartType === 'nyquist' && (
              <NyquistPlot
                testData={displayTestData}
                viewMode={state.viewMode}
                visibleChannels={state.visibleChannels}
                colorMap={colorMap}
                onPlotRef={(el) => { nyquistRef.current = el; }}
              />
            )}

            {displayTestData.length > 0 && state.chartType === 'combined' && (
              <CombinedPlot
                testData={displayTestData.slice(0, 1)}
                visibleChannels={state.visibleChannels}
                phaseUnit={state.phaseUnit}
                yScale={state.yScale}
                onPlotRef={(el) => { combinedRef.current = el; }}
              />
            )}
          </Box>}

          {/* Classification results — shown below chart whenever exactly one test is selected */}
          {classificationTestDetail?.pathogenClassificationResults &&
            classificationTestDetail.pathogenClassificationResults.length > 0 && (
              <Box
                ref={classificationPanelRef}
                sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider' }}
              >
                <ClassificationPanel
                  results={classificationTestDetail.pathogenClassificationResults}
                  channelColorMap={CHANNEL_COLOR_MAP}
                  referenceLabels={referenceLabelsMap}
                />
              </Box>
            )}

          {/* Controls */}
          <Box sx={{ flexShrink: 0 }}>
            <ChartControls
              chartType={state.chartType}
              onChartTypeChange={(v) => setState((prev) => ({ ...prev, chartType: v }))}
              selectedTestCount={selectedTestIds.length}
              viewMode={state.viewMode}
              onViewModeChange={(v) => setState((prev) => ({ ...prev, viewMode: v }))}
              visibleChannels={state.visibleChannels}
              onChannelToggle={handleChannelToggle}
              phaseUnit={state.phaseUnit}
              onPhaseUnitChange={(v) => setState((prev) => ({ ...prev, phaseUnit: v }))}
              yScale={state.yScale}
              onYScaleChange={(v) => setState((prev) => ({ ...prev, yScale: v }))}
              deltaMode={state.deltaMode}
              onDeltaModeChange={(v) => setState((prev) => ({ ...prev, deltaMode: v }))}
              showCalibrationTab={isResearcher && features.enableResearchFeatures}
            />
          </Box>
        </Box>
      </Box>

      {/* Hidden off-screen chart container for per-test export rendering */}
      {isExporting && hiddenExportTestId !== null && (
        <Box
          sx={{
            position: 'fixed',
            top: -9999,
            left: -9999,
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        >
          <Box sx={{ width: 1600, height: 700 }}>
            {hiddenExportViewType === 'bode' && (
              <BodePlot
                key={hiddenExportTestId}
                testData={hiddenExportTestData}
                viewMode={state.viewMode}
                visibleChannels={state.visibleChannels}
                phaseUnit={state.phaseUnit}
                yScale={state.yScale}
                colorMap={colorMap}
                onPlotRef={(el) => {
                  hiddenPlotRef.current = el;
                  if (el) hiddenPlotResolver.current?.();
                }}
              />
            )}
            {hiddenExportViewType === 'nyquist' && (
              <NyquistPlot
                key={hiddenExportTestId}
                testData={hiddenExportTestData}
                viewMode={state.viewMode}
                visibleChannels={state.visibleChannels}
                colorMap={colorMap}
                onPlotRef={(el) => {
                  hiddenPlotRef.current = el;
                  if (el) hiddenPlotResolver.current?.();
                }}
              />
            )}
            {hiddenExportViewType === 'combined' && (
              <CombinedPlot
                key={hiddenExportTestId}
                testData={hiddenExportTestData}
                visibleChannels={state.visibleChannels}
                phaseUnit={state.phaseUnit}
                yScale={state.yScale}
                onPlotRef={(el) => {
                  hiddenPlotRef.current = el;
                  if (el) hiddenPlotResolver.current?.();
                }}
              />
            )}
            {hiddenExportViewType === 'delta' && (
              <DeltaPlot
                key={hiddenExportTestId}
                testData={hiddenExportTestData}
                visibleChannels={state.visibleChannels}
                phaseUnit={state.phaseUnit}
                colorMap={colorMap}
                deltaMode={state.deltaMode}
                onPlotRef={(el) => {
                  hiddenPlotRef.current = el;
                  if (el) hiddenPlotResolver.current?.();
                }}
              />
            )}
          </Box>
          {hiddenClassificationResults && hiddenClassificationResults.length > 0 && (
            <Box ref={hiddenClassificationRef}>
              <ClassificationPanel
                results={hiddenClassificationResults}
                channelColorMap={CHANNEL_COLOR_MAP}
                referenceLabels={hiddenReferenceLabels}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Status bar */}
      <Box
        sx={{
          px: 2,
          py: 0.5,
          borderTop: 1,
          borderColor: 'divider',
          flexShrink: 0,
          display: 'flex',
          gap: 2,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {selectedTestIds.length} test{selectedTestIds.length !== 1 ? 's' : ''} selected
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {traceCount} traces
        </Typography>
      </Box>

      {/* Import Wizard */}
      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportComplete={() => {
          setImportOpen(false);
          queryClient.invalidateQueries({ queryKey: ['tests-for-analysis'] });
        }}
      />

      {/* Reference label fetch failure warning */}
      <Dialog
        open={labelFetchWarning !== null}
        onClose={() => { setLabelFetchWarning(null); setExportLoading(false); }}
      >
        <DialogTitle>Some reference labels could not be loaded</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Labels could not be fetched for the following test{labelFetchWarning?.failedTestIds.length !== 1 ? 's' : ''}:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            {labelFetchWarning?.failedTestIds.map((id) => (
              <li key={id}><Typography variant="body2">{id}</Typography></li>
            ))}
          </Box>
          <DialogContentText sx={{ mt: 1 }}>
            These channels will be exported with empty reference label values. You can proceed or cancel to retry.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLabelFetchWarning(null); setExportLoading(false); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={labelFetchWarning?.onProceed}>
            Export anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
